/**
 * / — the front door.
 *
 * What this serves is decided by platform_settings.root_mode, read from the
 * public endpoint:
 *
 *   'platform' (default) — this page, the multi-venue landing
 *   'venue'              — redirect to default_venue_id's public page, so a
 *                          single-venue deployment does not present itself as
 *                          a directory
 *
 * root_mode and default_venue_id are NOT in the public settings payload — they
 * are operational config, withheld deliberately (see
 * functions/api/platform/settings-public.js). The redirect below is therefore
 * written but dormant: it fires only if the payload ever carries those keys,
 * and until then the page renders platform mode. Marked here rather than
 * silently defaulted.
 *
 * This page does NOT use AppShell — it owns its whole viewport
 * (see routes.jsx). Deliberately art-free: type and space carry it, and there
 * is nothing to go stale (V2-DESIGN-REFERENCE.md §2.6).
 */

import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { apiGet } from '../lib/api.js';

const FEATURES = [
  {
    title: 'Live scoring',
    body: 'Kiosk tablets on each board, real-time scores on the TV and every phone in the room',
  },
  {
    title: 'Automated tournaments',
    body: 'Groups, knockouts, brackets, and standings — drawn and managed automatically',
  },
  {
    title: 'Player stats',
    body: 'Per-dart averages, 180s, checkouts, season standings, and a profile for every player',
  },
  {
    title: 'Multi-venue ready',
    body: 'One platform, as many venues as you need. Each with their own settings, seasons, and players',
  },
];

export default function Landing() {
  const [platform, setPlatform] = useState(null);

  useEffect(() => {
    let cancelled = false;

    apiGet('/api/platform/settings-public').then(({ data }) => {
      if (!cancelled) setPlatform(data?.settings ?? null);
    });

    return () => { cancelled = true; };
  }, []);

  // Dormant unless the endpoint starts returning root_mode — see the header.
  if (platform?.root_mode === 'venue' && platform?.default_venue_slug) {
    return <Navigate to={`/venues/${platform.default_venue_slug}`} replace />;
  }

  const name = platform?.platform_name ?? 'HopDarts';

  return (
    <div className="hd-landing">
      <header className="hd-landing-bar">
        <Link to="/" className="hd-brand" style={{ padding: 0 }}>{name}</Link>
        <div className="hd-topbar-spacer" />
        <Link to="/login" className="hd-btn hd-btn-quiet hd-btn-sm">Log in</Link>
      </header>

      <section className="hd-landing-section hd-hero">
        <h1 className="hd-hero-title">
          Welcome to <span className="hd-wordmark">{name}</span>
        </h1>
        <p className="hd-hero-tagline">
          League nights, live scoring, player stats — one platform for all your venues
        </p>
        <div className="hd-row hd-row-cta hd-hero-cta">
          <Link className="hd-btn hd-btn-primary" to="/register-venue">
            Register your venue
          </Link>
          <Link className="hd-btn hd-btn-secondary" to="/login">Log in</Link>
        </div>
      </section>

      <section className="hd-landing-section">
        <div className="hd-features">
          {FEATURES.map((f) => (
            <div key={f.title}>
              <h3 className="hd-feature-title">{f.title}</h3>
              <p className="hd-feature-body">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="hd-landing-section hd-landing-cta">
        <h2>Get started</h2>
        <p className="hd-landing-cta-body">
          Add your venue to the platform and start running your league nights properly
        </p>
        <div className="hd-row hd-row-cta" style={{ justifyContent: 'center' }}>
          <Link className="hd-btn hd-btn-primary" to="/register-venue">
            Register your venue
          </Link>
        </div>
      </section>

      <footer className="hd-landing-foot">
        <div className="hd-landing-foot-brand">{name}</div>
        <div className="hd-landing-foot-meta">
          © {new Date().getFullYear()} {name}
          {platform?.platform_contact_email && (
            <> · <a
              href={`mailto:${platform.platform_contact_email}`}
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >{platform.platform_contact_email}</a></>
          )}
        </div>
      </footer>
    </div>
  );
}
